import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, api, getToken, setToken } from "./api";
import type { User } from "./types";

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Signed in, but the server did not answer when we checked. */
  offline: boolean;
  retry: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  /** Replaces a password somebody else chose with your own. */
  chooseOwnPassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get<{ user: User }>("/auth/me")
      .then((r) => {
        setUser(r.user);
        setOffline(false);
      })
      .catch((err: unknown) => {
        // Only the server saying no ends a session. A server that says
        // nothing at all is a stopped server, and throwing the token away
        // there turns "start it again" into "sign in again, and wonder why".
        if (err instanceof ApiError && err.isOffline) {
          setOffline(true);
          return;
        }
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  /** `login` is an email or a mobile number. */
  const signIn = useCallback(async (login: string, password: string) => {
    const r = await api.post<{ token: string; user: User }>("/auth/login", { login, password });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const signUp = useCallback(async (login: string, password: string, name: string) => {
    const r = await api.post<{ token: string; user: User }>("/auth/register", { login, password, name });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const chooseOwnPassword = useCallback(async (newPassword: string) => {
    const r = await api.post<{ token: string; user: User }>("/auth/first-password", { newPassword });
    setToken(r.token);
    setUser(r.user);
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    setOffline(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, offline, retry, signIn, signUp, chooseOwnPassword, signOut }),
    [user, loading, offline, retry, signIn, signUp, chooseOwnPassword, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
